//@ts-check
import React, { useRef, useState } from "react";
import styles from "./members.module.scss";
import base from "../../styles/company.module.scss";
import NoSortIcon from "Assets/icon/icon-status-disabled.svg";
import UpSortIcon from "Assets/icon/icon-status-up.svg";
import DownSortIcon from "Assets/icon/icon-status-down.svg";
import { FetchResult, useMutation, useQuery } from "@apollo/client";
import { QUERY_COMPANY_MEMBERS_SCREEN } from "apps/company/api/graphQL/query";
import appInfo from "utility/app-info";
import { MEMBER_ACTION } from "./dataTypes";
import { memberStatusStyleMapper } from "./utils";
import _ from "lodash";
import { PaginationFooter } from "apps/company/components/common/pagination";
import { formatDate } from "apps/company/common/utils";
import { BUTTON_VARIANT } from "apps/company/common/constants";
import {
  MUTATION_DEACTIVATE_COMPANY_MEMBER,
  MUTATION_REACTIVATE_COMPANY_MEMBER,
  MUTATION_SEND_INVITE_COMPANY_MEMBER,
} from "apps/company/api/graphQL/mutation";
import TemplateDialog from "apps/company/components/common/modal/TemplateDialog";
import BulkUploadDialog from "apps/company/components/common/modal/BulkUploadDialog";
import { Button } from "components/ui";
import { PageLoading } from "apps/company/components/loading";
import {
  AddMemberDialog,
  StatusDialog,
} from "apps/company/components/common/modal";
import { Search, useSearch } from "components/ui/Search";
import { CommonModalProps } from "apps/company/components/common/modal/common";
import { useAuth } from "providers/auth";
import { WONE_DB_KEYS, membersDB } from "apps/company/data/app-db";
import PortalErrorPage from "apps/company/components/error";
import {
  Action,
  CompanyPortalMemberAdd,
  CompanyPortalMemberDeactivate,
  CompanyPortalMemberReactivate,
  CompanyPortalMemberSendInvite,
  MembersScreen,
  Members as MembersType,
} from "apps/user/learner/api/types";
import { logError } from "utility/Function";
import { Trans } from "react-i18next";
import { useTranslation } from "lib/i18n/use-translation";

const Members = () => {
  const { t } = useTranslation(["admin"]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [sort, setSort] = useState<boolean | null>(null);
  const [memberUserId, setMemberUserId] = useState<string>("");
  const [activeAction, setActiveAction] = useState<MEMBER_ACTION>();
  // MODAL STATES
  const [modalInfo, setModalInfo] =
    useState<Pick<CommonModalProps, "title" | "description" | "action">>();
  const [isTemplateModalOpen, setIsTemplateModalOpen] =
    useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] =
    useState<boolean>(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] =
    useState<boolean>(false);
  const title = useRef<string>(t("admin:nav.members"));
  const subtitle = useRef<string>("");
  const labels = useRef<string[]>([]);
  const addButton = useRef<Action>();
  const uploadButton = useRef<Action>();
  const members = useRef<MembersType[]>([]);

  //HOOKS
  const { searchQuery, onSubmitSearchQuery } = useSearch();

  const { loading, data, refetch, error } = useQuery(
    QUERY_COMPANY_MEMBERS_SCREEN,
    {
      variables: {
        appInfo,
        statusSort: sort,
        searchQuery: searchQuery,
        itemsPerPage: 8,
        pageNumber: currentPageNumber,
      },
    }
  );
  const { companyAdmin } = useAuth();

  const [deactivateMember] = useMutation(MUTATION_DEACTIVATE_COMPANY_MEMBER, {
    variables: {
      appInfo,
      memberUserId: "",
    },
  });
  const [reactivateMember] = useMutation(MUTATION_REACTIVATE_COMPANY_MEMBER, {
    variables: {
      appInfo,
      memberUserId: "",
    },
  });
  const [sendMemberInvite, { loading: loadingSendMemberInvite }] = useMutation(
    MUTATION_SEND_INVITE_COMPANY_MEMBER,
    {
      variables: {
        appInfo,
        memberUserId: "",
      },
    }
  );
  //Keep track of sort click
  const sortClickCount = useRef(0);
  const onSortClick = () => {
    if (sortClickCount.current > 3) {
      sortClickCount.current = 0;
    } else {
    }
    if (sortClickCount.current === 0) {
      setSort(true);
      refetch({ statusSort: true });
      sortClickCount.current = 1;
    } else if (sortClickCount.current === 1) {
      setSort(false);
      refetch({ statusSort: false });
      sortClickCount.current = 2;
    } else {
      setSort(null);
      refetch({ statusSort: null });
      sortClickCount.current = 0;
    }
  };

  if (loading && !searchQuery && members.current.length === 0) {
    return (
      <div className={styles["members-container"]}>
        <PageLoading />
      </div>
    );
  }
  if (error) {
    return <PortalErrorPage pageTitle={t("admin:nav.members")} />;
  }

  //HANDLERS
  const screenData = data?.companyPortalMembersScreen as MembersScreen;

  if (screenData) {
    title.current = screenData.title;
    subtitle.current = screenData.subTitle;
    labels.current = screenData.labels;
    uploadButton.current = screenData.uploadButton;
    addButton.current = screenData.addButton;
    members.current = screenData.members;
  }

  const onPageNumberClick = (pageNumber: number) => {
    if (searchQuery) {
      setCurrentPageNumber(pageNumber);
    } else {
      membersDB
        .setItem(WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER, pageNumber)
        .then(() => {
          refetch({ pageNumber });
        });
    }
  };

  const onPreviousClick = async () => {
    if (searchQuery) {
      setCurrentPageNumber((pageNumber) =>
        pageNumber > 1 ? pageNumber - 1 : 1
      );
    } else {
      const pageNumber: number = await membersDB.getItem(
        WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER
      );

      const updatedPageNumber = pageNumber > 1 ? pageNumber - 1 : 1;

      membersDB
        .setItem(WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER, updatedPageNumber)
        .then((pageNumber: number) => refetch({ pageNumber }));
    }
  };

  const onNextClick = async () => {
    if (searchQuery) {
      setCurrentPageNumber((pageNumber) =>
        pageNumber >= screenData?.totalPages
          ? screenData?.totalPages
          : pageNumber + 1
      );
    } else {
      const pageNumber: number = await membersDB.getItem(
        WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER
      );

      const updatedPageNumber =
        pageNumber >= screenData?.totalPages
          ? screenData?.totalPages
          : pageNumber + 1;

      membersDB
        .setItem(WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER, updatedPageNumber)
        .then((pageNumber: number) => refetch({ pageNumber }));
    }
  };

  const handleActionButton = ({
    action,
    memberUserId,
  }: {
    action: string;
    memberUserId: string;
  }) => {
    setMemberUserId(memberUserId);
    setActiveAction(action as MEMBER_ACTION);
    switch (action) {
      case MEMBER_ACTION.SEND_INVITE:
        setActiveAction(null);
        if (memberUserId) {
          sendMemberInvite({
            variables: {
              appInfo,
              memberUserId,
            },
          })
            .then((response) => {
              if (response.errors) {
                setModalInfo({
                  title: t("admin:error.defaultTitle"),
                  description: response.errors[0].message,
                  action: t("admin:action.close"),
                });
                setIsTemplateModalOpen(true);
              } else if (response.data) {
                const updatedMembers = (
                  response.data
                    .companyPortalMemberSendInvite as CompanyPortalMemberSendInvite
                ).companyAdmin.membersScreen.members;
                members.current = updatedMembers;
                setModalInfo({
                  title: t("admin:members.inviteSent"),
                  action: t("admin:action.close"),
                });
                setIsStatusModalOpen(true);
              }
            })
            .catch((error) => {
              logError("Error sending member invite", error);
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: error.message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            });
        }
        break;
      case MEMBER_ACTION.REACTIVATE:
        setModalInfo({
          title: t("admin:members.reactivate.title"),
          description: <Trans i18nKey="admin:members.reactivate.description" />,
          action: t("admin:action.reactivate"),
        });
        setIsTemplateModalOpen(true);
        break;
      case MEMBER_ACTION.DEACTIVATE:
        setModalInfo({
          title: t("admin:members.deactivate.title"),
          description: <Trans i18nKey="admin:members.deactivate.description" />,
          action: t("admin:action.deactivate"),
        });
        setIsTemplateModalOpen(true);
        break;
    }
  };

  const handleDialogButtonAction = async () => {
    switch (activeAction) {
      case MEMBER_ACTION.REACTIVATE:
        setActiveAction(null);
        if (memberUserId) {
          reactivateMember({
            variables: {
              appInfo,
              memberUserId,
            },
          })
            .then((response) => {
              if (response.errors) {
                setModalInfo({
                  title: t("admin:error.defaultTitle"),
                  description: response.errors[0].message,
                  action: t("admin:action.close"),
                });
                setIsTemplateModalOpen(true);
              } else if (response.data) {
                const updatedMembers = (
                  response.data
                    .companyPortalMemberReactivate as CompanyPortalMemberReactivate
                ).companyAdmin.membersScreen.members;
                members.current = updatedMembers;
                setModalInfo({
                  title: t("admin:members.reactivated"),
                  action: t("admin:action.close"),
                });
                setIsStatusModalOpen(true);
              }
            })
            .catch((error) => {
              logError("Error reactivating member", error);
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: error.message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            });
        }
        break;
      case MEMBER_ACTION.DEACTIVATE:
        setActiveAction(null);
        if (memberUserId) {
          deactivateMember({
            variables: {
              appInfo,
              memberUserId,
            },
          })
            .then((response) => {
              if (response.errors) {
                setModalInfo({
                  title: t("admin:error.defaultTitle"),
                  description: response.errors[0].message,
                  action: t("admin:action.close"),
                });
                setIsTemplateModalOpen(true);
              } else if (response.data) {
                const updatedMembers = (
                  response.data
                    .companyPortalMemberDeactivate as CompanyPortalMemberDeactivate
                ).companyAdmin.membersScreen.members;
                members.current = updatedMembers;
                setModalInfo({
                  title: t("admin:members.deactivated"),
                  action: t("admin:action.close"),
                });
                setIsStatusModalOpen(true);
              }
            })
            .catch((error) => {
              logError("Error deactivating member", error);
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: error.message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            });
        }

        break;
    }
  };

  const handleOnUserDialogAction = (response: FetchResult) => {
    const updatedMembers = (
      response.data.companyPortalMemberAdd as CompanyPortalMemberAdd
    ).companyAdmin.membersScreen.members;
    members.current = updatedMembers;
    /**
     * Call refetch() to update stale cached data causing a bug in which
     * the members table does not update
     ***/
    refetch();

    setModalInfo({
      title: t("admin:members.added"),
      action: t("admin:action.close"),
    });

    setIsStatusModalOpen(true);
  };

  const handleBulkDialogAction = () => {
    refetch();
  };

  //JSX

  membersDB.setItem(WONE_DB_KEYS.MEMBERS_TOTAL_PAGES, screenData?.totalPages);
  membersDB
    .getItem(WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER)
    .then((pageNumber: number) => {
      if (pageNumber) {
        if (!searchQuery) {
          setCurrentPageNumber(pageNumber);
        }
      } else {
        membersDB.setItem(WONE_DB_KEYS.MEMBERS_PAGINATION_NUMBER, 1);
      }
    });

  return (
    <>
      <AddMemberDialog
        onAddAction={handleOnUserDialogAction}
        onCloseAction={() => setIsAddMemberModalOpen(false)}
        isOpen={isAddMemberModalOpen}
      />

      <BulkUploadDialog
        isOpen={isBulkUploadModalOpen}
        exampleCSVURL={screenData?.exampleCsv}
        onAction={handleBulkDialogAction}
        onCloseAction={() => setIsBulkUploadModalOpen(false)}
      />
      <TemplateDialog
        isOpen={isTemplateModalOpen}
        title={modalInfo?.title}
        description={modalInfo?.description}
        action={modalInfo?.action}
        onCloseAction={() => setIsTemplateModalOpen(false)}
        onAction={handleDialogButtonAction}
      />
      <StatusDialog
        isOpen={isStatusModalOpen}
        title={modalInfo?.title}
        action={modalInfo?.action}
        onCloseAction={() => setIsStatusModalOpen(false)}
      />

      <div className={styles["members-container"]}>
        <div className={base["content"]}>
          <h1 className={base["main-title"]}>{title.current}</h1>
          <p
            className={base["main-subtitle"]}
            dangerouslySetInnerHTML={{ __html: subtitle.current }}
          ></p>
          <div className={styles["member-search"]}>
            <div className={styles["search-field"]}>
              <Search
                searchQuery={searchQuery}
                onSubmitSearchQuery={(searchString) => {
                  onSubmitSearchQuery(searchString);
                  setCurrentPageNumber(1);
                }}
                placeholder={t("admin:members.searchPlaceholder")}
                searchOnType
              />
            </div>
            <div className={styles["action-buttons"]}>
              <Button onClick={() => setIsBulkUploadModalOpen(true)}>
                {uploadButton.current?.buttonText ||
                  t("admin:members.bulkUploadButton")}
              </Button>
              <Button onClick={() => setIsAddMemberModalOpen(true)}>
                {addButton.current?.buttonText || t("admin:members.addButton")}
              </Button>
            </div>
          </div>
          <div className={styles["table-title"]}>
            {/* <h2 className="sans">ACTIVE</h2> */}
            <div className={styles["line"]}></div>
          </div>

          <table>
            <thead>
              <tr className="sans">
                {labels.current.map((header: string) =>
                  header.toLowerCase() === "status" ? (
                    <th
                      onClick={onSortClick}
                      className={styles["icon-header"]}
                      key={header}
                    >
                      {header}
                      <img
                        src={
                          sort
                            ? DownSortIcon
                            : sort === null
                            ? NoSortIcon
                            : UpSortIcon
                        }
                        alt="filter arrow"
                      ></img>
                    </th>
                  ) : (
                    <th key={header}>{header}</th>
                  )
                )}
              </tr>
            </thead>
            {loading && !data ? (
              <tbody>
                <tr>
                  <td colSpan={labels.current.length}>
                    <PageLoading />
                  </td>
                </tr>
              </tbody>
            ) : (
              <>
                <tbody>
                  {_.isEmpty(members.current) ? (
                    <tr>
                      <td colSpan={labels.current.length}>
                        <div className={styles["common-no-results"]}>
                          <h3>{t("admin:members.empty")}</h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    members.current.map((member) => (
                      <tr key={member.id}>
                        <td className={styles[member.status.toLowerCase()]}>
                          {member.member}
                        </td>
                        <td className={styles[member.status.toLowerCase()]}>
                          {member.firstName}
                        </td>
                        <td className={styles[member.status.toLowerCase()]}>
                          {member.lastName}
                        </td>
                        <td className={styles[member.status.toLowerCase()]}>
                          {member.email}
                        </td>
                        <td className={styles[member.status.toLowerCase()]}>
                          {member.timezone}
                        </td>
                        <td className={`${styles["chip"]} sans`}>
                          <div
                            className={
                              styles[
                                `chip--${memberStatusStyleMapper(
                                  member.status
                                )}`
                              ]
                            }
                          >
                            {member.status}
                          </div>
                        </td>
                        <td className={styles[member.status.toLowerCase()]}>
                          {formatDate(member.statusDate)}
                        </td>
                        {member.action.buttonStyle ===
                          BUTTON_VARIANT.DISABLED.toString() ||
                        companyAdmin.id === member.id ? (
                          <td className={`${styles["chip"]} sans`}>
                            <div className={styles["chip--default"]}>
                              {member.action.buttonText}
                            </div>
                          </td>
                        ) : loadingSendMemberInvite ? (
                          <td className={`${styles["action-wait"]} sans`}>
                            {member.action.buttonText}
                          </td>
                        ) : (
                          <td
                            onClick={() =>
                              handleActionButton({
                                action: member.action.buttonAction,
                                memberUserId: member.id,
                              })
                            }
                            className={`${styles["action"]} sans`}
                          >
                            {member.action.buttonText}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  {(!searchQuery ||
                    (searchQuery && members.current.length > 1)) && (
                    <tr>
                      <td colSpan={screenData?.labels.length}>
                        <PaginationFooter
                          classNames={styles["pagination"]}
                          numberOfPages={screenData?.totalPages}
                          currentActivePage={currentPageNumber}
                          onPageNumberClick={onPageNumberClick}
                          onPreviousClick={onPreviousClick}
                          onNextClick={onNextClick}
                        />
                      </td>
                    </tr>
                  )}
                </tfoot>
              </>
            )}
          </table>
        </div>
      </div>
    </>
  );
};

export default Members;
